/**
 * Bulgarian Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const bg: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Табло',
  'nav.funds': 'Фондове',
  'nav.investments': 'Инвестиции',
  'nav.investees': 'Получатели на инвестиции',
  'nav.esg': 'ESG Преглед',
  'nav.compliance': 'Съответствие',
  'nav.reports': 'Доклади',
  'nav.settings': 'Настройки',
  'nav.users': 'Потребители',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Индикатори',
  'nav.taxonomy': 'Таксономия на ЕС',
  'nav.filings': 'Подадени документи',
  'nav.calendar': 'Календар',
  'nav.submit': 'Подаване на данни',
  'nav.submissions': 'Моите подавания',
  'nav.benchmarks': 'Референтни показатели',
  'nav.messages': 'Съобщения',
  'nav.portfolio': 'Портфолио',
  'nav.performance': 'Резултати на фондове',
  'nav.esgSummary': 'ESG Обобщение',
  'nav.pipeline': 'Конвейер',
  'nav.dueDiligence': 'Надлежна проверка',
  'nav.impact': 'Показатели за въздействие',
  'nav.sdg': 'Картиране на ЦУР',
  'nav.audit': 'Одитна следа',
  'nav.snapshots': 'Моментни снимки',
  'nav.knowledgeGraph': 'Граф на знанието',
  'nav.geographic': 'Географски',
  'nav.bi': 'BI Студио',
  'nav.ai': 'AI Асистент',
  'nav.tasks': 'Задачи',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Регулаторен монитор',

  // Common Actions
  'action.create': 'Създаване',
  'action.edit': 'Редактиране',
  'action.delete': 'Изтриване',
  'action.save': 'Запазване',
  'action.cancel': 'Отмяна',
  'action.submit': 'Подаване',
  'action.approve': 'Одобряване',
  'action.reject': 'Отхвърляне',
  'action.download': 'Изтегляне',
  'action.export': 'Експортиране',
  'action.import': 'Импортиране',
  'action.filter': 'Филтриране',
  'action.search': 'Търсене',
  'action.refresh': 'Обновяване',
  'action.close': 'Затваряне',
  'action.back': 'Назад',
  'action.next': 'Напред',
  'action.previous': 'Предишен',
  'action.viewAll': 'Преглед на всички',
  'action.viewDetails': 'Преглед на детайли',
  'action.generate': 'Генериране',
  'action.signOut': 'Изход',
  'action.addNew': 'Добавяне',
  'action.invite': 'Покана',

  // Statuses
  'status.draft': 'Чернова',
  'status.submitted': 'Подадено',
  'status.validated': 'Валидирано',
  'status.approved': 'Одобрено',
  'status.rejected': 'Отхвърлено',
  'status.pending': 'Изчакващо',
  'status.inProgress': 'В процес',
  'status.overdue': 'Просрочено',
  'status.compliant': 'Съответстващо',
  'status.filed': 'Подадено',
  'status.active': 'Активно',
  'status.inactive': 'Неактивно',
  'status.closed': 'Затворено',
  'status.fundraising': 'Набиране на средства',
  'status.investing': 'Инвестиране',
  'status.harvesting': 'Прибиране на реколтата',
  'status.validating': 'Валидиране',
  'status.review': 'В преглед',
  'status.monitoring': 'Мониторинг',
  'status.exiting': 'Излизане',
  'status.pipeline': 'Тръбопровод',
  'status.due_diligence': 'Надлежна проверка',
  'status.disbursed': 'Изплатено',
  'status.restructured': 'Преструктурирано',
  'status.matured': 'Падежирано',
  'status.written_off': 'Отписано',
  'status.exited': 'Излязъл',
  'status.incomplete': 'Непълно',
  'status.open': 'Отворено',
  'status.in_progress': 'В процес',
  'status.completed': 'Завършено',
  'status.cancelled': 'Анулирано',
  'status.accepted': 'Прието',
  'status.failed': 'Неуспешно',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Преглед на портфолиото',
  'dashboard.complianceStatus': 'Статус на съответствие',
  'dashboard.collectionProgress': 'Напредък на събирането',
  'dashboard.activityFeed': 'Активност',
  'dashboard.quickActions': 'Бързи действия',
  'dashboard.investmentPipeline': 'Инвестиционен конвейер',
  'dashboard.totalFunds': 'Общо фондове',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Активни инвестиции',
  'dashboard.totalInvestees': 'Получатели на инвестиции',
  'dashboard.recentActivity': 'Скорошна активност',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Общо ангажирано',
  'lp.capitalCalled': 'Изискан капитал',
  'lp.distributed': 'Разпределено',
  'lp.netPosition': 'Нетна позиция',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Резултати на фондове',
  'lp.esgSummary': 'ESG Обобщение',
  'lp.reportLibrary': 'Библиотека с доклади',
  'lp.taxonomyAligned': 'Съответствие с таксономията',
  'lp.paiCoverage': 'PAI Покритие',
  'lp.portfolioPerformance': 'Резултати на LP портфолиото',

  // Fund Labels
  'fund.name': 'Име на фонда',
  'fund.code': 'Код на фонда',
  'fund.vintage': 'Година на създаване',
  'fund.size': 'Размер на фонда',
  'fund.targetSize': 'Целеви размер',
  'fund.currency': 'Валута',
  'fund.strategy': 'Стратегия',
  'fund.status': 'Статус',
  'fund.sfdrCategory': 'SFDR Категория',
  'fund.geographyFocus': 'Географски фокус',
  'fund.sectorFocus': 'Секторен фокус',

  // Form Labels
  'form.name': 'Име',
  'form.email': 'Имейл',
  'form.country': 'Държава',
  'form.amount': 'Сума',
  'form.currency': 'Валута',
  'form.date': 'Дата',
  'form.description': 'Описание',
  'form.notes': 'Бележки',
  'form.type': 'Тип',
  'form.period': 'Период',
  'form.year': 'Година',
  'form.required': 'Задължително',
  'form.optional': 'По избор',

  // Table Headers
  'table.fund': 'Фонд',
  'table.commitment': 'Ангажимент',
  'table.called': 'Изискано',
  'table.distributed': 'Разпределено',
  'table.tvpi': 'TVPI',
  'table.status': 'Статус',
  'table.regulation': 'Регулация',
  'table.filingType': 'Тип документ',
  'table.reportingPeriod': 'Период',
  'table.createdAt': 'Създаден',
  'table.actions': 'Действия',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'Таксономия на ЕС',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Пълнота на данните',
  'compliance.nextDeadline': 'Следващ краен срок',

  // Reports
  'report.quarterly': 'Тримесечен доклад',
  'report.annual': 'Годишен доклад',
  'report.paiStatement': 'PAI Декларация',
  'report.taxonomyReport': 'Доклад по таксономия',
  'report.generatedAt': 'Генерирано',
  'report.version': 'Версия',

  // General
  'general.loading': 'Зареждане...',
  'general.noData': 'Няма налични данни',
  'general.error': 'Възникна грешка',
  'general.retry': 'Опитайте отново',
  'general.total': 'Общо',
  'general.page': 'Страница',
  'general.of': 'от',
  'general.showing': 'Показване на',
  'general.results': 'резултати',
  'general.language': 'Език',
  'general.english': 'Английски',
  'general.french': 'Френски',

  // Tour
  'tour.takeTour': 'Направете обиколка',
  'tour.skip': 'Пропусни',
  'tour.next': 'Напред',
  'tour.prev': 'Назад',
  'tour.finish': 'Завърши',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Навигация',
  'tour.sidebarContent': 'Използвайте страничната лента за навигация между управление на портфолио, събиране на ESG данни, съответствие, отчети и аналитични инструменти.',
  'tour.kpisTitle': 'Преглед на портфолио',
  'tour.kpisContent': 'Тези карти показват портфолиото ви с един поглед — общо фондове, AUM, активни инвестиции и брой на инвестираните.',
  'tour.actionsTitle': 'Бързи действия',
  'tour.actionsContent': 'Създавайте фондове, добавяйте инвестиции, канете субекти и генерирайте отчети с един клик.',
  'tour.languageTitle': 'Език',
  'tour.languageContent': 'Превключвайте между 24 официални езика на ЕС. Целият интерфейс се превежда незабавно.',
  'tour.aiTitle': 'AI Асистент',
  'tour.aiContent': 'Задавайте въпроси за портфолиото си по всяко време. AI асистентът има пълен контекст за фондовете и инвестициите ви.',
  'action.createFund': 'Създай фонд',
  'action.editFund': 'Редактирай фонд',
  'action.addInvestment': 'Добави инвестиция',
  'action.addInvestee': 'Добави субект',
  'action.editInvestee': 'Редактирай субект',
  'action.createTemplate': 'Създай шаблон',
  'action.createSubmission': 'Създай подаване',
  'action.createFiling': 'Създай подаване',
  'action.generateReport': 'Генерирай отчет',
  'action.inviteUser': 'Покани потребител',
  'action.sendInvitation': 'Изпрати покана',
  'action.saveChanges': 'Запази промените',
  'form.fundName': 'Име на фонд',
  'form.fundCode': 'Код на фонд',
  'form.sfdrCategory': 'SFDR Категория',
  'form.baseCurrency': 'Базова валута',
  'form.targetSize': 'Целеви размер',
  'form.vintageYear': 'Година на учредяване',
  'form.geographyFocus': 'Географски фокус',
  'form.sectorFocus': 'Секторен фокус',
  'form.investmentCode': 'Код на инвестиция',
  'form.instrumentType': 'Тип инструмент',
  'form.maturityDate': 'Дата на падеж',
  'form.sector': 'Сектор',
  'form.countryCode': 'Код на държава',
  'form.entityType': 'Тип субект',
  'form.region': 'Регион',
  'form.staffCount': 'Брой служители',
  'form.borrowers': 'Кредитополучатели',
  'form.contactName': 'Име за контакт',
  'form.contactEmail': 'Имейл за контакт',
  'form.templateName': 'Име на шаблон',
  'form.templateType': 'Тип шаблон',
  'form.indicators': 'Индикатори',
  'form.reportingPeriod': 'Отчетен период',
  'form.reportingYear': 'Отчетна година',
  'form.regulation': 'Регулация',
  'form.filingType': 'Тип подаване',
  'form.reportType': 'Тип отчет',
  'form.emailAddress': 'Имейл адрес',
  'form.role': 'Роля',
  'form.selectFund': 'Изберете фонд...',
  'form.selectInvestee': 'Изберете субект...',
  'table.code': 'Код',
  'table.investee': 'Субект',
  'table.instrument': 'Инструмент',
  'table.amount': 'Сума',
  'table.currency': 'Валута',
  'table.maturity': 'Падеж',
  'table.name': 'Име',
  'table.country': 'Държава',
  'table.type': 'Тип',
  'table.period': 'Период',
  'table.completion': 'Завършване',
  'table.submitted': 'Подадено',
  'table.version': 'Версия',
  'table.indicators': 'Индикатори',
  'table.dueDate': 'Краен срок',
  'table.generated': 'Генерирано',
  'table.email': 'Имейл',
  'table.role': 'Роля',
  'error.createFailed': 'Създаването е неуспешно. Опитайте отново.',
  'error.updateFailed': 'Актуализацията е неуспешна. Опитайте отново.',
  'error.loadFailed': 'Зареждането на данни е неуспешно.',

  // Sidebar Sections
  'sections.portfolio': 'ПОРТФОЛИО',
  'sections.dataCollection': 'СЪБИРАНЕ НА ДАННИ',
  'sections.intelligence': 'ИНТЕЛИГЕНТНОСТ',
  'sections.ecosystem': 'ЕКОСИСТЕМА',
  'sections.admin': 'АДМИНИСТРАЦИЯ',
  'sections.other': 'ДРУГИ',

  // Page Titles
  'pages.messagesTitle': 'Съобщения',
  'pages.taxonomyTitle': 'Таксономия на ЕС',
  'pages.biStudioTitle': 'BI Studio',
  'pages.knowledgeGraphTitle': 'Граф на знанията',
  'pages.benchmarksTitle': 'Бенчмаркове',
  'pages.mcpTitle': 'MCP интеграция',
  'pages.tasksTitle': 'Задачи',
  'pages.submitDataTitle': 'Изпратете ESG данни',

  // Empty States
  'empty.benchmarksComingSoon': 'Бенчмаркове очаквайте скоро',

  // Labels
  'labels.sfdr': 'SFDR',
  'labels.euTaxonomy': 'Таксономия на ЕС',
  'labels.csrd': 'CSRD',
  'labels.mifid': 'MiFID',
  'labels.other': 'Друго',
  'labels.critical': 'Критичен',
  'labels.high': 'Висок',
  'labels.medium': 'Среден',
  'labels.low': 'Нисък',

  // Count / summary strings
  'count.fundsTotal': '{count} fund(s) total',
  'count.investmentsTotal': '{count} investment(s) total',
  'count.investeesTotal': '{count} investee(s) total',

  // Filter options
  'filter.allStatus': 'All Status',
  'filter.allSfdr': 'All SFDR',

  // Empty states
  'empty.noFundsYet': 'No funds yet. Create your first fund.',
  'empty.noFundsMatch': 'No funds match your filters.',
  'empty.noInvestmentsYet': 'No investments yet.',
  'empty.noInvestmentsMatch': 'No investments match your search.',
  'empty.noInvesteesYet': 'No investees yet.',
  'empty.noInvesteesMatch': 'No investees match your search.',
  'empty.noTemplatesYet': 'No ESG templates configured yet.',
  'empty.noSubmissionsYet': 'No ESG submissions yet.',
  'empty.noComplianceData': 'No SFDR compliance data available.',
  'empty.noFilingsYet': 'No compliance filings yet.',
  'empty.noReportsYet': 'No reports generated yet.',
  'empty.selectFundPai': 'Select a fund to view PAI indicators.',
  'empty.noPaiIndicators': 'No PAI indicators found for this fund.',
  'empty.selectFundCalendar': 'Select a fund to view compliance calendar.',
  'empty.noCalendarEvents': 'No upcoming events for this fund.',

  // Page subtitles
  'subtitle.esg': 'Templates and data submissions',
  'subtitle.compliance': 'SFDR, PAI indicators, and regulatory filings',
  'subtitle.reports': 'Generate and manage compliance reports',
  'subtitle.settings': 'Manage your account and preferences',

  // Tab labels
  'tab.templates': 'Templates',
  'tab.submissions': 'Submissions',
  'tab.sfdr': 'SFDR',
  'tab.paiIndicators': 'PAI Indicators',
  'tab.filings': 'Filings',
  'tab.reports': 'Reports',
  'tab.calendar': 'Calendar',

  // Compliance table headers
  'table.indicator': 'Indicator',
  'table.category': 'Category',
  'table.metric': 'Metric',
  'table.value': 'Value',
  'table.mandatory': 'Mandatory',
  'table.data': 'Data',
  'table.deadline': 'Deadline',
  'table.taxonomy': 'Taxonomy',

  // Yes/No
  'general.yes': 'Yes',
  'general.no': 'No',

  // Error messages
  'error.loadTemplates': 'Failed to load templates.',
  'error.loadSubmissions': 'Failed to load submissions.',
  'error.loadCompliance': 'Failed to load compliance data.',
  'error.loadPai': 'Failed to load PAI indicators.',
  'error.loadFilings': 'Failed to load filings.',
  'error.loadCalendar': 'Failed to load calendar events.',
  'error.createTemplate': 'Failed to create template. Please try again.',
  'error.createSubmission': 'Failed to create submission. Please try again.',
  'error.createFiling': 'Failed to create filing. Please try again.',
  'error.generateReport': 'Failed to generate report.',
  'error.templateNameRequired': 'Template name is required',
  'error.selectFund': 'Select a fund',
  'error.selectInvestee': 'Select an investee',
  'error.validAmount': 'Enter a valid amount',
  'error.reportingPeriodRequired': 'Reporting period is required',
  'error.validYearRequired': 'Valid year is required',
  'error.enterPeriod': 'Enter a period',
  'error.nameRequired': 'Name is required',
  'error.countryCodeRequired': 'Country code is required',
  'error.operationFailed': 'Operation failed.',
  'error.pleaseselectFund': 'Please select a fund',

  // Success messages
  'success.submissionCreated': 'Submission created successfully.',
  'success.profileSaved': 'Profile saved successfully.',

  // Relative time
  'time.justNow': 'Just now',
  'time.minutesAgo': '{count}m ago',
  'time.hoursAgo': '{count}h ago',
  'time.daysAgo': '{count}d ago',

  // Settings
  'settings.profile': 'Profile',
  'settings.organization': 'Organization',
  'settings.apiKeys': 'API Keys',
  'settings.notifications': 'Notifications',
  'settings.orgId': 'Org ID',
  'settings.tier': 'Tier',
  'settings.apiKeysDescription': 'Contact your organization administrator to manage API keys and integration credentials.',
  'settings.apiKeysNote': 'API keys provide programmatic access to Impact Core data. For security, key creation and revocation is restricted to organization admins.',
  'settings.notifReportAlerts': 'Report generation alerts',
  'settings.notifReportAlertsDesc': 'Email when a report is generated or fails',
  'settings.notifComplianceReminders': 'Compliance deadline reminders',
  'settings.notifComplianceRemindersDesc': 'Email reminders for upcoming filing deadlines',
  'settings.notifEsgUpdates': 'ESG submission updates',
  'settings.notifEsgUpdatesDesc': 'Email when investee submissions change status',
  'settings.notifInAppActivity': 'In-app activity feed',
  'settings.notifInAppActivityDesc': 'Show real-time activity notifications in header',

  // Data Completeness label
  'compliance.dataCompletenessLabel': 'Data Completeness',
};
